export function formatDateForFilter(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().split('T')[0];
}

export function isWithinDateRange(value, startDate, endDate) {
  const normalized = formatDateForFilter(value);
  if (!startDate && !endDate) return true;
  if (!normalized) return false;
  if (startDate && normalized < startDate) return false;
  if (endDate && normalized > endDate) return false;
  return true;
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function exportRowsToExcel(filename, title, headers, rows) {
  const headerHtml = headers.map((header) => `<th>${escapeHtml(header)}</th>`).join('');
  const rowsHtml = rows
    .map((row) => `<tr>${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join('')}</tr>`)
    .join('');

  const html = `
    <html>
      <head>
        <meta charset="utf-8" />
      </head>
      <body>
        <table border="1">
          <tr><th colspan="${headers.length}">${escapeHtml(title)}</th></tr>
          <tr>${headerHtml}</tr>
          ${rowsHtml}
        </table>
      </body>
    </html>
  `;

  const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.xls`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function exportRowsToPdf(title, headers, rows) {
  const headerHtml = headers.map((header) => `<th>${escapeHtml(header)}</th>`).join('');
  const rowsHtml = rows
    .map((row) => `<tr>${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join('')}</tr>`)
    .join('');

  const printWindow = window.open('', '_blank', 'width=1200,height=800');
  if (!printWindow) return;

  printWindow.document.write(`
    <html>
      <head>
        <title>${escapeHtml(title)}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 24px; color: #0f172a; }
          h1 { font-size: 20px; margin-bottom: 16px; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; }
          th, td { border: 1px solid #cbd5e1; padding: 8px; text-align: left; }
          th { background: #eff6ff; }
        </style>
      </head>
      <body>
        <h1>${escapeHtml(title)}</h1>
        <table>
          <thead><tr>${headerHtml}</tr></thead>
          <tbody>${rowsHtml}</tbody>
        </table>
      </body>
    </html>
  `);
  printWindow.document.close();
  printWindow.focus();
  printWindow.print();
}

export function openDonationReceiptPdf({
  title,
  receiptCode,
  generatedAt,
  summary,
  sections = [],
  footerNote,
}) {
  const printWindow = window.open('', '_blank', 'width=920,height=1100');
  if (!printWindow) return false;

  const sectionsHtml = sections
    .map((section) => {
      const itemsHtml = (section.items || [])
        .map(
          (item) => `
            <tr>
              <td>${escapeHtml(item.label)}</td>
              <td>${escapeHtml(item.value)}</td>
            </tr>
          `
        )
        .join('');

      return `
        <section class="section">
          <h2>${escapeHtml(section.title || '-')}</h2>
          <table>
            <tbody>${itemsHtml}</tbody>
          </table>
        </section>
      `;
    })
    .join('');

  printWindow.document.write(`
    <html>
      <head>
        <meta charset="utf-8" />
        <title>${escapeHtml(title || 'Struk Donasi')}</title>
        <style>
          :root {
            color-scheme: light;
          }
          * {
            box-sizing: border-box;
          }
          body {
            margin: 0;
            padding: 28px;
            font-family: Arial, sans-serif;
            color: #0f172a;
            background: #f8fafc;
          }
          .sheet {
            max-width: 820px;
            margin: 0 auto;
            background: #ffffff;
            border: 1px solid #dbeafe;
            border-radius: 20px;
            overflow: hidden;
          }
          .hero {
            padding: 28px;
            background: linear-gradient(135deg, #0f172a, #1d4ed8);
            color: #ffffff;
          }
          .eyebrow {
            margin: 0;
            font-size: 11px;
            letter-spacing: 0.24em;
            text-transform: uppercase;
            color: rgba(255, 255, 255, 0.75);
          }
          h1 {
            margin: 10px 0 0;
            font-size: 28px;
            line-height: 1.2;
          }
          .meta {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 12px;
            margin-top: 18px;
          }
          .meta-card {
            padding: 14px 16px;
            border-radius: 14px;
            background: rgba(255, 255, 255, 0.12);
            border: 1px solid rgba(255, 255, 255, 0.14);
          }
          .meta-card span {
            display: block;
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 0.14em;
            color: rgba(255, 255, 255, 0.75);
          }
          .meta-card strong {
            display: block;
            margin-top: 8px;
            font-size: 15px;
            line-height: 1.5;
          }
          .content {
            padding: 24px 28px 28px;
          }
          .summary {
            margin: 0 0 18px;
            padding: 16px 18px;
            border-radius: 16px;
            background: #eff6ff;
            color: #1e3a8a;
            font-size: 14px;
            line-height: 1.7;
          }
          .section {
            margin-top: 18px;
          }
          .section h2 {
            margin: 0 0 10px;
            font-size: 15px;
            text-transform: uppercase;
            letter-spacing: 0.14em;
            color: #1d4ed8;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            border: 1px solid #e2e8f0;
            border-radius: 14px;
            overflow: hidden;
          }
          td {
            padding: 11px 14px;
            border-bottom: 1px solid #e2e8f0;
            font-size: 13px;
            vertical-align: top;
          }
          tr:last-child td {
            border-bottom: none;
          }
          td:first-child {
            width: 34%;
            background: #f8fafc;
            color: #475569;
            font-weight: 600;
          }
          .footer {
            margin-top: 22px;
            padding-top: 16px;
            border-top: 1px dashed #cbd5e1;
            font-size: 12px;
            line-height: 1.6;
            color: #64748b;
          }
          @media print {
            body {
              padding: 0;
              background: #ffffff;
            }
            .sheet {
              border: none;
              border-radius: 0;
            }
          }
        </style>
      </head>
      <body>
        <div class="sheet">
          <div class="hero">
            <p class="eyebrow">Struk Donasi</p>
            <h1>${escapeHtml(title || 'Struk Konfirmasi Donasi')}</h1>
            <div class="meta">
              <div class="meta-card">
                <span>Kode Struk / Transaksi</span>
                <strong>${escapeHtml(receiptCode || '-')}</strong>
              </div>
              <div class="meta-card">
                <span>Dibuat Pada</span>
                <strong>${escapeHtml(generatedAt || '-')}</strong>
              </div>
            </div>
          </div>
          <div class="content">
            ${summary ? `<p class="summary">${escapeHtml(summary)}</p>` : ''}
            ${sectionsHtml}
            ${
              footerNote
                ? `<div class="footer">${escapeHtml(footerNote)}</div>`
                : ''
            }
          </div>
        </div>
      </body>
    </html>
  `);

  printWindow.document.close();
  printWindow.focus();
  printWindow.print();
  return true;
}

function escapePdfText(value) {
  return String(value ?? '')
    .replace(/\\/g, '\\\\')
    .replace(/\(/g, '\\(')
    .replace(/\)/g, '\\)')
    .replace(/\r?\n/g, ' ');
}

function wrapPdfText(text, maxCharsPerLine = 88) {
  const normalized = String(text ?? '').replace(/\s+/g, ' ').trim();
  if (!normalized) return ['-'];

  const words = normalized.split(' ');
  const lines = [];
  let currentLine = '';

  words.forEach((word) => {
    const nextLine = currentLine ? `${currentLine} ${word}` : word;
    if (nextLine.length > maxCharsPerLine) {
      if (currentLine) {
        lines.push(currentLine);
      }
      currentLine = word;
    } else {
      currentLine = nextLine;
    }
  });

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines;
}

function buildReceiptPdfLines({ title, receiptCode, generatedAt, summary, sections = [], footerNote }) {
  const lines = [
    title || 'Struk Donasi',
    '',
    `Kode Struk / Transaksi: ${receiptCode || '-'}`,
    `Dibuat Pada: ${generatedAt || '-'}`,
    '',
  ];

  wrapPdfText(summary || '').forEach((line) => lines.push(line));
  lines.push('');

  sections.forEach((section) => {
    lines.push(String(section.title || '-').toUpperCase());
    (section.items || []).forEach((item) => {
      wrapPdfText(`${item.label || '-'}: ${item.value || '-'}`).forEach((line) => lines.push(line));
    });
    lines.push('');
  });

  if (footerNote) {
    wrapPdfText(footerNote).forEach((line) => lines.push(line));
  }

  return lines;
}

export function buildDonationReceiptPdfBlob(payload) {
  const lines = buildReceiptPdfLines(payload);
  const pageWidth = 595;
  const pageHeight = 842;
  const leftMargin = 48;
  const topStart = 794;
  const lineHeight = 16;
  const bottomMargin = 48;

  let currentY = topStart;
  const contentParts = ['BT', '/F1 11 Tf'];

  lines.forEach((line) => {
    if (currentY <= bottomMargin) {
      contentParts.push('ET');
      contentParts.push('BT');
      contentParts.push('/F1 11 Tf');
      currentY = topStart;
    }

    contentParts.push(`1 0 0 1 ${leftMargin} ${currentY} Tm (${escapePdfText(line)}) Tj`);
    currentY -= lineHeight;
  });

  contentParts.push('ET');

  const streamContent = contentParts.join('\n');
  const pdfObjects = [
    '1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj',
    '2 0 obj\n<< /Type /Pages /Kids [3 0 R] /Count 1 >>\nendobj',
    `3 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Resources << /Font << /F1 4 0 R >> >> /Contents 5 0 R >>\nendobj`,
    '4 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>\nendobj',
    `5 0 obj\n<< /Length ${streamContent.length} >>\nstream\n${streamContent}\nendstream\nendobj`,
  ];

  let pdfContent = '%PDF-1.4\n';
  const offsets = [0];

  pdfObjects.forEach((objectText) => {
    offsets.push(pdfContent.length);
    pdfContent += `${objectText}\n`;
  });

  const xrefOffset = pdfContent.length;
  pdfContent += `xref\n0 ${pdfObjects.length + 1}\n`;
  pdfContent += '0000000000 65535 f \n';
  offsets.slice(1).forEach((offset) => {
    pdfContent += `${String(offset).padStart(10, '0')} 00000 n \n`;
  });
  pdfContent += `trailer\n<< /Size ${pdfObjects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;

  return new Blob([pdfContent], { type: 'application/pdf' });
}
